import React, { useState, useEffect, useMemo, useRef } from "react"
import debounce from "lodash.debounce"
import { getTranslatableField, constructTranslatableProductLink } from '../../helpers/utils'
import cookie from "react-cookies"

export default function ProductSearch({ appProps, keyword, autoFocus }) {
  const [searchResults, setSearchResults] = useState({ products: [], fallbackProducts: [], similar: [], popular: [], categories: [] })
  const [successfulSearches, setSuccessfulSearches] = useState([])
  const [showEmptyProductsBox, setShowEmptyProductsBox] = useState(false)
  const [searchValue, setSearchValue] = useState(keyword || '')
  const [isLoading, setIsLoading] = useState(false)

  const successfulSearchesRef = useRef(successfulSearches)
  const searchRequestIdRef = useRef(0)

  const onUnmountOrUnload = searchesArray => {
    return () => {
      if (appProps.advancedSearchAllowed) {
        const searchesToSave = []
        searchesArray.sort((a, b) => a.length > b.length ? 1 : -1)

        while (searchesArray.length) {
          if (!searchesArray.some((search, index) => index !== 0 && search.includes(searchesArray[0]))) {
            searchesToSave.push(searchesArray[0])
          }
          searchesArray.shift()
        }

        updateCookie(searchesToSave)

        if(searchesToSave.length > 0) {
          let fd = new FormData()
          fd.append('searches_to_save', JSON.stringify(searchesToSave))

          Rails.ajax({
            type: "POST",
            url: `/update_search_terms`,
            contentType: "application/json; charset=UTF-8",
            dataType: "json",
            data: fd
          })
        }
      }
    }

  }

  useEffect(() => {
    const handleUnload = onUnmountOrUnload(successfulSearches.slice())

    successfulSearchesRef.current = successfulSearches
    window.addEventListener("beforeunload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleUnload)
    }
  }, [successfulSearches])

  useEffect(() => {
    return () => {
      onUnmountOrUnload(successfulSearchesRef.current.slice())()
    }
  }, [])

  const debouncedSearch = useMemo(() => debounce((value, requestId) => {
    Rails.ajax({
      type: "GET",
      url: `${appProps.productSearchPath}?keyword=${encodeURIComponent(value)}`,
      contentType: "application/json; charset=UTF-8",
      dataType: "json",
      success: res => {
        if (requestId !== searchRequestIdRef.current) return

        const products = res.products || []
        setSearchResults({
          products,
          fallbackProducts: res.fallback_products || [],
          similar: res.similar || [],
          popular: res.popular || [],
          categories: res.categories || []
        })
        setShowEmptyProductsBox(true)
        setIsLoading(false)

        if (products.length > 0) {
          setSuccessfulSearches(previousSearches => [...previousSearches, value])
        }
      },
      error: () => {
        if (requestId === searchRequestIdRef.current) setIsLoading(false)
      }
    })
  }, 500), [appProps.productSearchPath])

  useEffect(() => {
    return () => {
      searchRequestIdRef.current += 1
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  function updateCookie(keywords) {
    const recentSearchCookie = cookie.load('recentSearches') || [];

    keywords.forEach(keyword => {
      const idx = recentSearchCookie.findLastIndex(item => item.toLowerCase() === keyword.toLowerCase())
      recentSearchCookie.unshift(keyword)
      if (idx > -1) {
        recentSearchCookie.splice(idx + 1, 1)
      } else {
        if (recentSearchCookie.length > 5) {
          recentSearchCookie.pop()
        }
      }
    })

    cookie.save('recentSearches', recentSearchCookie, { path: '/' })
  }

  const handleAutocompleteInput = (event) => {
    let value = event.target.value.trim()
    const requestId = searchRequestIdRef.current + 1
    searchRequestIdRef.current = requestId
    setSearchValue(value)

    if (value.length > 1) {
      setIsLoading(true)
      setShowEmptyProductsBox(false)
      debouncedSearch(value, requestId)
    } else {
      debouncedSearch.cancel()
      setIsLoading(false)
      setSearchResults({ products: [], fallbackProducts: [], similar: [], popular: [], categories: [] })
      setShowEmptyProductsBox(false)
    }
  }

  const renderProductItem = (product, index) => {
    let imageEntity = product.images[0]

    return (
      <a href={ constructTranslatableProductLink(product, appProps.currentLocale, appProps.defaultLocale, appProps.localeUrlPresent) } className="product-item" key={index} rel="nofollow noindex">
        <div className="image-wrapper">
          <img src={ imageEntity ? imageEntity.thumbnail : 'https://storage.googleapis.com/reactive_files_eu/no-image.jpg' } />
        </div>

        <div className="details-wrapper">
          <div className="presentation">
            { getTranslatableField(product, 'title', appProps.currentLocale, false) }
          </div>
          <div className="prices-wrapper">
            <div className="display-price">
              { product.displayTotalPrice }
            </div>
            { product.onSale &&
              ( appProps.showTextInsteadOfCompareToPrice ?
                <div className="compare-to-price-wrapper">
                  <div className='compare-to-price-text'>{ appProps.translations.products.discount_price }</div>
                </div> :
                <div className="compare-to-price-wrapper">
                  <div className="compare-to-price">{ product.displayTotalCompareToPrice }</div>
                  <div className="compare-to-price-percent">{`(-${Math.round(product.onSalePercentage)}%)`}</div>
                </div>
              )
            }
          </div>
          <div className="sku">
            { appProps.translations.products.sku }: <b>{ product.sku }</b>
          </div>
        </div>
      </a>
    )
  }

  const renderSearchBlocks = (category, idx) => {
    let items = []
    switch (category) {
      case 'recent':
        items = cookie.load('recentSearches') || []
        break
      case 'similar':
        items = searchResults.similar
        break
      case 'popular':
        items = searchResults.popular
    }

    return items.length > 0 && (
      <div className={`search-block flex-box flex-column ${category}-searches`} key={ idx }>
        <div className='title flex-box flex-column'>
          { appProps.translations.product_search[`${category}_searches`] }
        </div>
        <div className='flex-box flex-column'>
          { items.map((item) => (
            <a href={`${appProps.productsPath}?keyword=${item}`} key={item} rel='noindex nofollow noreferrer'>
              { item }
            </a>
          ))}
        </div>
      </div>
    )
  }

  const noResultSuggestions = () => {
    const recentSearches = cookie.load('recentSearches') || []
    const suggestions = [...recentSearches, ...searchResults.similar, ...searchResults.popular]
    return [...new Set(suggestions.filter(item => item && item.toLowerCase() !== searchValue.toLowerCase()))].slice(0, 6)
  }

  const noProductsFoundText = () => {
    const value = searchValue.trim()
    if (!value) return appProps.translations.products.no_products_found

    return `${appProps.translations.products.no_products_found_for_keyword || 'No products found for search term: '}${value}`
  }

  return (
    <div className={`search-box-wrapper ${appProps.advancedSearchAllowed ? 'advanced-search' : 'basic-search'} ${appProps.headerVersion == '6' ? 'header-v6-search' : ''}`}>
      <div className="search-input-wrapper">
        <div className="search-input">
          <input
            autoFocus
            type="search"
            className="search-box"
            placeholder={ appProps.translations.products.search_products }
            onChange={ handleAutocompleteInput }
            defaultValue={ keyword || '' }
            aria-busy={ isLoading }
            onKeyPress={(event) => {
              if(event.charCode == 13) {
                const value = event.target.value

                if(value.length > 1) {
                  window.location.href = `${appProps.productsPath}?keyword=${value}`
                }
              }
            }}
          />
          <i className="search_icon fa-light fa-search" aria-hidden="true"></i>
          { isLoading ?
            <span className="search-loader" role="status" aria-label="Loading search results">
              <span className="search-loader-spinner" />
            </span> :
            <i className="search_arrow fa-light fa-chevron-right" aria-hidden="true" onClick={() => {
              window.location.href = `${appProps.productsPath}?keyword=${searchValue}`
            }}></i>
          }
        </div>

        { (searchResults.products.length > 0 || (searchResults.products.length === 0 && showEmptyProductsBox)) &&
          <>
            <div className={`search-results flex-box flex-column ${searchResults.products.length > 0 ? "animated fadeIn" : ""}`}>
              <div className='search-results-main flex-box'>
                <div className="products-block flex-1">
                  { searchResults.products.length > 0 &&
                    <>
                      { searchResults.products.map(renderProductItem) }
                    </>
                  }
                  { searchResults.products.length === 0 &&
                    <div className='no-search-results'>
                      <div className='no-search-results-title'>
                        { noProductsFoundText() }
                      </div>
                      { searchResults.fallbackProducts.length > 0 &&
                        <div className='fallback-products'>
                          <div className='fallback-products-title'>
                            { appProps.translations.product_search?.recommended_products || 'Recommended products' }
                          </div>
                          { searchResults.fallbackProducts.map(renderProductItem) }
                        </div>
                      }
                      { searchResults.fallbackProducts.length === 0 && noResultSuggestions().length > 0 &&
                        <div className='no-search-results-suggestions'>
                          { noResultSuggestions().map(item => (
                            <a href={`${appProps.productsPath}?keyword=${item}`} key={item} rel='noindex nofollow noreferrer'>
                              { item }
                            </a>
                          ))}
                        </div>
                      }
                    </div>
                  }
                </div>
                { appProps.advancedSearchAllowed &&
                  <div className="searches-block">
                    { ["recent", "similar", "popular"].map(renderSearchBlocks) }
                  </div>
                }
              </div>
              { appProps.advancedSearchAllowed && searchResults.categories.length > 0 &&
                <div className='flex-box flex-column categories-block-wrapper'>
                  <div className='categories-title'>{`${appProps.translations.product_search.found_in_categories}:`}</div>
                  <div className="categories-block flex-box flex-wrap items-center content-start">
                    { searchResults.categories.map((category, idx) => (
                      <a
                        key={idx}
                        className='category-search-result button inverted flex-box'
                        href={`${appProps.productsPath}/c/${category.slug}?keyword=${successfulSearches.slice(-1)}`}>
                          <div>{ category.title }</div>
                          <div>({ category.count })</div>
                      </a>
                    ))}
                  </div>
                </div>
              }
            </div>
          </>
        }
      </div>
    </div>
  )
}
